/**
 * KenoReportsAdminTab
 * Per-variant financial dashboard for the two in-house Keno games.
 * Sits under Admin → Keno → Reports.  Backed by /api/admin/keno-reports.
 *
 * Shows for both variants (4 Hearts Fire Ball · Super Double-Up):
 *   HANDLE · WINS PAID · NET HOLD · HOLD % · ROUNDS · UNIQUE PLAYERS · AVG BET
 *   BONUS TRIGGERS · FREE-PLAY ROUNDS · PROGRESSIVE HITS · Superball hits (SD only)
 *   Daily NET sparkline, current progressive pool value, top 10 winners.
 *   CSV export button for round-level detail.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Download, Sparkles, TrendingUp, TrendingDown, Trophy, Radio, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WINDOWS = [
    { label: 'Today',   days: 1 },
    { label: '7 days',  days: 7 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
];

const VARIANTS = [
    {
        key: 'hearts',
        label: '4 Hearts Fire Ball Keno',
        accent: '#ff5a5a',
        extraMetrics: [
            { field: 'bonus_triggers',     label: 'Bonus Triggers',     hint: '4-hearts hits that started BETO free plays' },
            { field: 'free_play_rounds',   label: 'Free-Play Rounds',   hint: 'House-funded rounds inside a bonus session' },
            { field: 'free_plays_awarded', label: 'Free Plays Awarded', hint: 'Total free plays given out across triggers' },
            { field: 'retriggers',         label: 'Retriggers',         hint: 'Bonus retriggers during free plays' },
            { field: 'progressive_hits',   label: 'Progressive Hits',   hint: 'Times the 4-Hearts progressive was won' },
        ],
    },
    {
        key: 'super_double',
        label: 'Super Double-Up Keno',
        accent: '#4bd0ff',
        extraMetrics: [
            { field: 'double_ups',       label: 'Double-Ups Chosen',  hint: 'Rounds where the player pushed the Double button' },
            { field: 'superball_hits',   label: 'Superball Hits',     hint: 'Times the 20th ball matched a selection (×4)' },
            { field: 'bonus_triggers',   label: 'Bonus Triggers',     hint: 'Rounds that started Super Free Plays' },
            { field: 'free_play_rounds', label: 'Free-Play Rounds',   hint: 'House-funded Super Free Play rounds' },
            { field: 'retriggers',       label: 'Retriggers',         hint: '+5 free-play retriggers awarded' },
            { field: 'progressive_hits', label: 'Progressive Hits',   hint: 'Times the Super Double progressive was won' },
        ],
    },
];

const money = (n) => {
    const v = Number(n || 0);
    const s = v < 0 ? '-' : '';
    return `${s}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pct = (n) => `${Number(n || 0).toFixed(2)}%`;
const int = (n) => Number(n || 0).toLocaleString();

// SVG sparkline — daily NET across the window
const Sparkline = ({ points, accent }) => {
    if (!points || points.length === 0) {
        return <div className="text-[10px] text-white/40 italic h-[46px] flex items-center">No activity in window</div>;
    }
    const w = 260, h = 46, pad = 3;
    const vals = points.map(p => p.net);
    const min = Math.min(0, ...vals);
    const max = Math.max(0, ...vals);
    const range = max - min || 1;
    const step = (w - pad * 2) / Math.max(1, points.length - 1);
    const yFor = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
    const zeroY = yFor(0);
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * step} ${yFor(p.net)}`).join(' ');
    return (
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
            <line x1={pad} x2={w - pad} y1={zeroY} y2={zeroY} stroke="rgba(255,255,255,0.15)" strokeDasharray="2 3" />
            <path d={d} fill="none" stroke={accent} strokeWidth="1.5" />
            {points.map((p, i) => (
                <circle key={i} cx={pad + i * step} cy={yFor(p.net)} r="2" fill={accent} />
            ))}
        </svg>
    );
};

// Stacked hourly HANDLE bar chart (hearts + super-double), last N hours
const HourlyBars = ({ buckets }) => {
    if (!buckets || buckets.length === 0) {
        return <div className="text-[11px] text-white/40 italic h-[130px] flex items-center justify-center">No hourly data yet.</div>;
    }
    const W = 720, H = 130, padL = 6, padR = 6, padTop = 14, padBottom = 18;
    const n = buckets.length;
    const maxHandle = Math.max(1, ...buckets.map(b => b.handle));
    const plotW = W - padL - padR;
    const plotH = H - padTop - padBottom;
    const slot = plotW / n;
    const barW = Math.max(2, slot * 0.62);
    const yFor = (v) => padTop + plotH - (v / maxHandle) * plotH;
    const HEARTS = '#ff5a5a', SD = '#4bd0ff';

    return (
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="keno-live-hourly-chart">
            {/* baseline */}
            <line x1={padL} x2={W - padR} y1={padTop + plotH} y2={padTop + plotH} stroke="rgba(255,255,255,0.18)" />
            {buckets.map((b, i) => {
                const x = padL + i * slot + (slot - barW) / 2;
                const heartsH = (b.hearts_handle / maxHandle) * plotH;
                const sdH = (b.super_double_handle / maxHandle) * plotH;
                const base = padTop + plotH;
                const isLast = i === n - 1;
                return (
                    <g key={b.hour_ct}>
                        {/* super-double (bottom) */}
                        {sdH > 0 && (
                            <rect x={x} y={base - sdH} width={barW} height={sdH} fill={SD} opacity={isLast ? 1 : 0.85} rx="1">
                                <title>{`${b.hour_label} · Super Double $${b.super_double_handle.toFixed(2)}`}</title>
                            </rect>
                        )}
                        {/* hearts (stacked on top) */}
                        {heartsH > 0 && (
                            <rect x={x} y={base - sdH - heartsH} width={barW} height={heartsH} fill={HEARTS} opacity={isLast ? 1 : 0.85} rx="1">
                                <title>{`${b.hour_label} · 4 Hearts $${b.hearts_handle.toFixed(2)}`}</title>
                            </rect>
                        )}
                        {/* transparent full-height hover target */}
                        <rect x={padL + i * slot} y={padTop} width={slot} height={plotH} fill="transparent">
                            <title>{`${b.hour_label} · Total played $${b.handle.toFixed(2)} · ${b.rounds} rounds`}</title>
                        </rect>
                        {/* hour label every 4th + last */}
                        {(i % 4 === 0 || isLast) && (
                            <text x={padL + i * slot + slot / 2} y={H - 5} fontSize="9" fill="rgba(255,255,255,0.45)" textAnchor="middle">{b.hour_label}</text>
                        )}
                    </g>
                );
            })}
        </svg>
    );
};

const LiveStat = ({ label, value, accent, testid, sub }) => (
    <div className="rounded-xl bg-white/5 p-3 text-center" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
        <div className="text-lg font-black" style={{ color: accent || '#fff' }}>{value}</div>
        {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
);

const LiveSection = ({ live, auto, setAuto, updatedAt }) => {
    const l = live?.last_60_min?.combined || {};
    const t = live?.today?.combined || {};
    const lh = live?.last_60_min?.hearts || {};
    const lsd = live?.last_60_min?.super_double || {};

    return (
        <div
            className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 p-4 space-y-3"
            data-testid="keno-live-section"
        >
            <div className="flex flex-wrap items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <Radio size={16} className="text-emerald-300" />
                <h3 className="text-white font-black text-sm tracking-wide" data-testid="keno-live-title">Live Play</h3>
                {updatedAt && <span className="text-[10px] text-white/40">updated {updatedAt}</span>}
                <button
                    onClick={() => setAuto(a => !a)}
                    className={`ml-auto px-3 py-1 text-xs rounded-full transition ${auto ? 'bg-emerald-400 text-black font-black' : 'bg-white/10 text-white/70 hover:text-white'}`}
                    data-testid="keno-live-auto-toggle"
                >
                    {auto ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
                </button>
            </div>

            {/* Last 60 min */}
            <div>
                <div className="flex items-center gap-1 mb-1.5 text-[11px] uppercase tracking-widest text-emerald-200/80">
                    <Clock size={12} /> Last 60 minutes
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <LiveStat label="Played (Handle)" value={money(l.handle)} accent="#fff" testid="keno-live-60-handle"
                        sub={`${int(l.rounds)} rounds`} />
                    <LiveStat label="Wins Paid" value={money(l.wins_paid)} accent="#fff" testid="keno-live-60-paid" />
                    <LiveStat label="Net Hold" value={money(l.net)} accent={(l.net || 0) >= 0 ? '#34d399' : '#fb7185'} testid="keno-live-60-net" />
                    <LiveStat label="Hold %" value={pct(l.hold_pct)} accent="#fff" testid="keno-live-60-hold" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="rounded-lg bg-white/5 px-3 py-1.5 flex items-center justify-between">
                        <span className="text-[11px] text-rose-300 font-semibold">4 Hearts · played</span>
                        <span className="text-white text-sm font-black" data-testid="keno-live-60-hearts-handle">{money(lh.handle)}</span>
                    </div>
                    <div className="rounded-lg bg-white/5 px-3 py-1.5 flex items-center justify-between">
                        <span className="text-[11px] text-cyan-300 font-semibold">Super Double · played</span>
                        <span className="text-white text-sm font-black" data-testid="keno-live-60-sd-handle">{money(lsd.handle)}</span>
                    </div>
                </div>
            </div>

            {/* Today so far */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <LiveStat label="Today · Played" value={money(t.handle)} accent="#fbbf24" testid="keno-live-today-handle"
                    sub={`${int(t.rounds)} rounds`} />
                <LiveStat label="Today · Wins Paid" value={money(t.wins_paid)} accent="#fff" testid="keno-live-today-paid" />
                <LiveStat label="Today · Net Hold" value={money(t.net)} accent={(t.net || 0) >= 0 ? '#34d399' : '#fb7185'} testid="keno-live-today-net" />
                <LiveStat label="Today · Hold %" value={pct(t.hold_pct)} accent="#fff" testid="keno-live-today-hold" />
            </div>

            {/* Hourly chart */}
            <div className="rounded-xl bg-black/30 p-3">
                <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] uppercase tracking-widest text-white/60">Played per hour (last 24h)</div>
                    <div className="flex items-center gap-3 text-[10px]">
                        <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#ff5a5a' }} /> 4 Hearts</span>
                        <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#4bd0ff' }} /> Super Double</span>
                    </div>
                </div>
                <HourlyBars buckets={live?.hourly} />
            </div>
        </div>
    );
};


const VariantCard = ({ variant, data }) => {
    if (!data) return null;
    const positive = data.net >= 0;
    return (
        <div
            className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3"
            style={{ boxShadow: `0 0 22px -14px ${variant.accent}` }}
            data-testid={`keno-report-card-${variant.key}`}
        >
            <div className="flex items-center gap-2">
                <Sparkles size={18} style={{ color: variant.accent }} />
                <h3 className="text-white font-black text-sm tracking-wide">{variant.label}</h3>
                {positive ? (
                    <span className="ml-auto text-xs text-emerald-400 flex items-center gap-1"><TrendingUp size={12}/> House +</span>
                ) : (
                    <span className="ml-auto text-xs text-rose-400 flex items-center gap-1"><TrendingDown size={12}/> Players +</span>
                )}
            </div>

            {/* Money row */}
            <div className="grid grid-cols-4 gap-2 text-center">
                <div className="rounded-md bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Handle</div>
                    <div className="text-white text-sm font-black" data-testid={`keno-${variant.key}-handle`}>{money(data.handle)}</div>
                </div>
                <div className="rounded-md bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Wins Paid</div>
                    <div className="text-white text-sm font-black" data-testid={`keno-${variant.key}-paid`}>{money(data.wins_paid)}</div>
                </div>
                <div className="rounded-md bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Net Hold</div>
                    <div className={`text-sm font-black ${positive ? 'text-emerald-400' : 'text-rose-400'}`} data-testid={`keno-${variant.key}-net`}>{money(data.net)}</div>
                </div>
                <div className="rounded-md bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Hold %</div>
                    <div className="text-white text-sm font-black" data-testid={`keno-${variant.key}-hold`}>{pct(data.hold_pct)}</div>
                </div>
            </div>

            {/* Counts row */}
            <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Rounds</div>
                    <div className="text-white text-sm font-black" data-testid={`keno-${variant.key}-rounds`}>{int(data.rounds)}</div>
                </div>
                <div className="rounded-md bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Players</div>
                    <div className="text-white text-sm font-black" data-testid={`keno-${variant.key}-players`}>{int(data.unique_players)}</div>
                </div>
                <div className="rounded-md bg-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Avg Bet</div>
                    <div className="text-white text-sm font-black" data-testid={`keno-${variant.key}-avgbet`}>{money(data.avg_bet)}</div>
                </div>
            </div>

            {/* Sparkline */}
            <div className="rounded-md bg-white/5 p-2">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Daily Net</div>
                <Sparkline points={data.daily} accent={variant.accent} />
            </div>

            {/* Keno-specific counts */}
            <div className="grid grid-cols-2 gap-2">
                {variant.extraMetrics.map(m => (
                    <div key={m.field} className="flex items-center justify-between rounded-md bg-white/5 px-2 py-1.5">
                        <span className="text-[11px] uppercase tracking-wider text-white/60" title={m.hint}>{m.label}</span>
                        <span className="text-white text-sm font-black">{int(data[m.field])}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const KenoReportsAdminTab = () => {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [live, setLive] = useState(null);
    const [auto, setAuto] = useState(true);
    const [liveUpdatedAt, setLiveUpdatedAt] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/keno-reports?days=${days}`, { withCredentials: true });
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Keno reports');
        } finally {
            setLoading(false);
        }
    }, [days]);

    const loadLive = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/keno-reports/live?hours=24`, { withCredentials: true });
            setLive(res.data);
            setLiveUpdatedAt(new Date().toLocaleTimeString());
        } catch (e) {
            // stay silent on background poll failures to avoid toast spam
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadLive(); }, [loadLive]);

    // Auto-refresh the live view every 15s when enabled
    useEffect(() => {
        if (!auto) return;
        const id = setInterval(loadLive, 15000);
        return () => clearInterval(id);
    }, [auto, loadLive]);

    const csvUrl = useMemo(
        () => `${API}/admin/keno-reports/csv?days=${days}&variant=all`,
        [days]
    );

    const combined = data?.combined || {};
    const variants = data?.variants || {};
    const top = data?.top_winners || [];
    const prog = data?.progressive || {};

    return (
        <div
            className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5 space-y-4"
            data-testid="keno-reports-panel"
        >
            {/* Header + controls */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                    <Sparkles size={20} className="text-amber-300" />
                    <h2 className="text-white text-lg font-black tracking-wide" data-testid="keno-reports-title">Keno Reports</h2>
                </div>
                <div className="flex items-center gap-1 rounded-full bg-white/5 p-1 ml-auto">
                    {WINDOWS.map(w => (
                        <button
                            key={w.days}
                            onClick={() => setDays(w.days)}
                            className={`px-3 py-1 text-xs rounded-full transition ${
                                days === w.days ? 'bg-amber-300 text-black font-black' : 'text-white/70 hover:text-white'
                            }`}
                            data-testid={`keno-reports-window-${w.days}`}
                        >{w.label}</button>
                    ))}
                </div>
                <button
                    onClick={load}
                    className="flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1 text-xs text-white transition disabled:opacity-50"
                    disabled={loading}
                    data-testid="keno-reports-refresh"
                >
                    <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
                </button>
                <a
                    href={csvUrl}
                    className="flex items-center gap-1 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 px-3 py-1 text-xs text-emerald-200 transition border border-emerald-500/40"
                    data-testid="keno-reports-csv"
                >
                    <Download size={12} /> CSV
                </a>
            </div>

            {/* Live real-time play */}
            <LiveSection live={live} auto={auto} setAuto={setAuto} updatedAt={liveUpdatedAt} />

            {/* Combined banner */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Combined Handle</div>
                    <div className="text-white text-lg font-black" data-testid="keno-combined-handle">{money(combined.handle)}</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Combined Wins Paid</div>
                    <div className="text-white text-lg font-black" data-testid="keno-combined-paid">{money(combined.wins_paid)}</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Combined Net</div>
                    <div className={`text-lg font-black ${(combined.net || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="keno-combined-net">
                        {money(combined.net)}
                    </div>
                </div>
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Combined Hold %</div>
                    <div className="text-white text-lg font-black" data-testid="keno-combined-hold">{pct(combined.hold_pct)}</div>
                </div>
                <div className="rounded-xl bg-white/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Combined Rounds</div>
                    <div className="text-white text-lg font-black" data-testid="keno-combined-rounds">{int(combined.rounds)}</div>
                </div>
            </div>

            {/* Progressive banner */}
            <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-3 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-rose-200">4-Hearts Progressive</div>
                        <div className="text-rose-100 text-lg font-black" data-testid="keno-hearts-progressive">{money(prog.hearts_pool)}</div>
                    </div>
                    <Sparkles size={26} className="text-rose-300" />
                </div>
                <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 p-3 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-cyan-200">Super Double Progressive</div>
                        <div className="text-cyan-100 text-lg font-black" data-testid="keno-sd-progressive">{money(prog.super_double_pool)}</div>
                    </div>
                    <Sparkles size={26} className="text-cyan-300" />
                </div>
            </div>

            {/* Per-variant cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {VARIANTS.map(v => (
                    <VariantCard key={v.key} variant={v} data={variants[v.key]} />
                ))}
            </div>

            {/* Top winners */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
                <div className="flex items-center gap-2 mb-2">
                    <Trophy size={16} className="text-amber-300" />
                    <h3 className="text-white text-sm font-black tracking-wide">Top 10 Winners (window)</h3>
                </div>
                {top.length === 0 ? (
                    <div className="text-white/40 text-xs italic px-2 py-4">No winners in this window.</div>
                ) : (
                    <table className="w-full text-xs" data-testid="keno-top-winners">
                        <thead>
                            <tr className="text-white/50">
                                <th className="text-left py-1">#</th>
                                <th className="text-left py-1">Player</th>
                                <th className="text-right py-1">Wagered</th>
                                <th className="text-right py-1">Won</th>
                                <th className="text-right py-1">Net</th>
                                <th className="text-right py-1">Rounds</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top.map((w, i) => (
                                <tr key={w.user_id} className="border-t border-white/5">
                                    <td className="py-1 text-white/60">{i + 1}</td>
                                    <td className="py-1 text-white font-semibold">{w.username}</td>
                                    <td className="py-1 text-right text-white/80">{money(w.handle)}</td>
                                    <td className="py-1 text-right text-white/80">{money(w.paid)}</td>
                                    <td className={`py-1 text-right font-black ${w.net_to_player >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                        {money(w.net_to_player)}
                                    </td>
                                    <td className="py-1 text-right text-white/60">{int(w.rounds)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {data?.window && (
                <div className="text-[10px] text-white/40 text-right">
                    Window: {new Date(data.window.start_ct).toLocaleString()} → {new Date(data.window.end_ct).toLocaleString()} (CT)
                </div>
            )}
        </div>
    );
};

export default KenoReportsAdminTab;
